import { createBrowserRouter, Navigate } from 'react-router';
import type { RouteObject } from 'react-router';
import type { ReactElement } from 'react';
import { OverviewPage } from '../pages/OverviewPage';
import { YearOverviewPage } from '../pages/YearOverviewPage';
import { WeekOverviewPage } from '../pages/WeekOverviewPage';
import { EconomicsPage } from '../pages/EconomicsPage';
import { ConfigurationPage } from '../pages/ConfigurationPage';
import { TreatmentPlanBuilderPage } from '../pages/TreatmentPlanBuilderPage';
import { InterventionsPage } from '../pages/InterventionsPage';
import { SettingsPage } from '../pages/SettingsPage';
import { BookingsPage } from '../pages/BookingsPage';
import { ErrorPage } from '../pages/ErrorPage';
import { RootLayout } from '../pages/RootLayout';
import { PermissionRouteGuard } from '../components/PermissionRouteGuard/PermissionRouteGuard';
import { SettingsRedirect } from '../components/SettingsRedirect/SettingsRedirect';
import { PatientsPage } from '../pages/PatientsPage';
import { RoomsSettings } from '../components/RoomsSettings/RoomsSettings';
import { InterventionRoomsSettings } from '../components/InterventionRoomsSettings/InterventionRoomsSettings';
import { RoomBookings } from '../components/RoomBookings/RoomBookings';
import { StaffBookings } from '../components/StaffBookings/StaffBookings';
import { ClosedDayBookings } from '../components/ClosedDayBookings/ClosedDayBookings';
import { ReservedStaffTimeBookings } from '../components/ReservedStaffTimeBookings/ReservedStaffTimeBookings';
import { PatientBookings } from '../components/PatientBookings/PatientBookings';
import { RoomNeedsSettings } from '../components/RoomNeedsSettings/RoomNeedsSettings';
import { StaffCompetencesSettings } from '../components/StaffCompetencesSettings/StaffCompetencesSettings';
import { StaffSettings } from '../components/StaffSettings/StaffSettings';
import { InterventionRoomPropertiesSettings } from '../components/InterventionRoomPropertiesSettings/InterventionRoomPropertiesSettings';
import { UsersSettings } from '../components/UsersSettings/UsersSettings';
import { LocationsSettings } from '../components/LocationsSettings/LocationsSettings';
import { OpeningDaysSettings } from '../components/OpeningDaysSettings/OpeningDaysSettings';
import ROUTES, {
  GLOBAL_SETTINGS_PERMISSIONS,
  LOCATION_SEGMENTS,
  LOCATION_SETTINGS_PERMISSIONS,
  LOCATION_VIEW_PERMISSIONS
} from './routeConstants';
import { PERMISSIONS, type Permission } from '../constants/permissions';

// A pathless guard route around `route`, so the page sits behind the permission
// the backend requires on the endpoints it calls.
const guarded = (permission: Permission, ...children: RouteObject[]): RouteObject => ({
  element: <PermissionRouteGuard permission={permission} />,
  children
});

// The settings subpages are guarded one by one from the same table the settings
// menu reads, so the menu and the router cannot disagree about who may open what.
const guardedSettingsPages = (
  pages: readonly { segment: string; permission: Permission }[],
  elementBySegment: Record<string, ReactElement>
): RouteObject[] =>
  pages.map(({ segment, permission }) =>
    guarded(permission, { path: segment, element: elementBySegment[segment] })
  );

const LOCATION_SETTINGS_ELEMENTS: Record<string, ReactElement> = {
  [ROUTES.settingsRooms]: <RoomsSettings />,
  [ROUTES.settingsInterventionRooms]: <InterventionRoomsSettings />,
  [ROUTES.settingsEmployees]: <StaffSettings />,
  [ROUTES.settingsOpeningDays]: <OpeningDaysSettings />
};

const GLOBAL_SETTINGS_ELEMENTS: Record<string, ReactElement> = {
  [ROUTES.settingsLocations]: <LocationsSettings />,
  [ROUTES.settingsRoomNeeds]: <RoomNeedsSettings />,
  [ROUTES.settingsInterventionRoomProperties]: <InterventionRoomPropertiesSettings />,
  [ROUTES.settingsStaffCompetences]: <StaffCompetencesSettings />,
  [ROUTES.settingsUsers]: <UsersSettings />
};

export const routes: RouteObject[] = [
  {
    path: ROUTES.root,
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.locations} replace />
      },
      {
        // The landing page is now the Overview: every readable location for a
        // selected year, against that year's target. The URL keeps the
        // /locations path so existing links still resolve; renaming the route
        // (and the year overview to "year plan") is tracked separately.
        path: ROUTES.locations,
        element: <OverviewPage />
      },
      // Location-scoped views. Each sits behind the READ permission of the
      // endpoints it calls; the write controls inside are gated separately.
      guarded(LOCATION_VIEW_PERMISSIONS[LOCATION_SEGMENTS.yearOverview], {
        path: ROUTES.yearOverview,
        element: <YearOverviewPage />
      }),
      guarded(LOCATION_VIEW_PERMISSIONS[LOCATION_SEGMENTS.weekOverview], {
        path: ROUTES.weekOverview,
        element: <WeekOverviewPage />
      }),
      guarded(LOCATION_VIEW_PERMISSIONS[LOCATION_SEGMENTS.economics], {
        path: ROUTES.economics,
        element: <EconomicsPage />
      }),
      // Patients is part of the daily planning work, so it lives in the main
      // navigation rather than under settings.
      guarded(LOCATION_VIEW_PERMISSIONS[LOCATION_SEGMENTS.patients], {
        path: ROUTES.patients,
        element: <PatientsPage />
      }),
      // Location-scoped settings — rooms, intervention rooms, employees, opening
      // days. Guarded per page, each on its own backend permission (opening days
      // on the account-scoped LOCATION_ADMIN); a role can hold one without the
      // others.
      {
        path: ROUTES.locationSettings,
        element: <SettingsPage />,
        children: [
          {
            index: true,
            element: <SettingsRedirect />
          },
          ...guardedSettingsPages(LOCATION_SETTINGS_PERMISSIONS, LOCATION_SETTINGS_ELEMENTS)
        ]
      },
      // Location-scoped bookings. Every booking endpoint requires BOOKING_READ
      // to list and BOOKING_WRITE to change, both at the location in the URL,
      // so the subtree is guarded on the read half.
      guarded(PERMISSIONS.BookingRead, {
        path: ROUTES.locationBookings,
        element: <BookingsPage />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.bookingsRoomBookings} replace />
          },
          {
            path: ROUTES.bookingsRoomBookings,
            element: <RoomBookings />
          },
          {
            path: ROUTES.bookingsStaffBookings,
            element: <StaffBookings />
          },
          {
            path: ROUTES.bookingsClosedDays,
            element: <ClosedDayBookings />
          },
          {
            path: ROUTES.bookingsReservedStaffTime,
            element: <ReservedStaffTimeBookings />
          },
          {
            path: ROUTES.bookingsPatientBookings,
            element: <PatientBookings />
          }
        ]
      }),
      // Account-level (global) configuration. These pages exist to edit the
      // account's treatment plan and intervention templates, so they are guarded
      // on TREATMENT_PLAN_TEMPLATES_WRITE rather than on the READ half — a
      // reader has nothing to do here.
      guarded(
        PERMISSIONS.TreatmentPlanTemplatesWrite,
        {
          path: ROUTES.configuration,
          element: <ConfigurationPage />
        },
        {
          path: 'configuration/treatment-plans',
          element: <Navigate to={`/${ROUTES.configuration}`} replace />
        },
        {
          path: ROUTES.treatmentPlanBuilder,
          element: <TreatmentPlanBuilderPage />
        },
        {
          path: ROUTES.configurationInterventions,
          element: <InterventionsPage />
        }
      ),
      // Account-level (global) settings — same layout as location settings, but
      // only the account-wide subpages live here. Guarded per page: managing
      // locations, managing users and each kind of shared configuration are
      // separate permissions on the backend.
      {
        path: ROUTES.settings,
        element: <SettingsPage />,
        children: [
          {
            index: true,
            element: <SettingsRedirect />
          },
          ...guardedSettingsPages(GLOBAL_SETTINGS_PERMISSIONS, GLOBAL_SETTINGS_ELEMENTS)
        ]
      },
      {
        path: ROUTES.notFound,
        element: <ErrorPage />
      },
      {
        path: ROUTES.errorPage,
        element: <ErrorPage />
      }
    ]
  }
];

const router = createBrowserRouter(routes, { basename: '/' });

export default router;
